import { type ReactElement } from 'react';
import { BuildsTable } from '@/features/builds/components/BuildsTable';
import { DashboardDateRangeControls } from '@/features/dashboard/components/DashboardDateRangeControls';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { ExecutionMetricsChartPanel } from '@/features/executions/components/ExecutionMetricsChartPanel';
import { MetricValueCardsRow } from '@/features/metrics/components/MetricValueCardsRow';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { PipelineDetailsPanels } from '@/features/pipelines/components/PipelineDetailsPanels';
import { DetailDeleteHeaderAction } from '@/features/shared/components/DetailDeleteHeaderAction';
import { s } from '@/theme/tokens';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { Panel } from '@/ui/composites/Panel';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { PageHeader } from '@/ui/layout/PageHeader';
import { Stack } from '@/ui/layout/Stack';
import { Box, Text } from '@/ui/primitives';
import type { PipelineDetailPageViewModel } from '@/features/pipelines/hooks/usePipelineDetailPageViewModel';
import type { PipelineDetailDto } from '@flowqa/shared';

type PipelineDetailContentProps = {
  pipeline: PipelineDetailDto;
  viewModel: PipelineDetailPageViewModel;
  onDelete?: () => Promise<void> | void;
  deleteLoading?: boolean;
};

export const PipelineDetailContent = ({
  pipeline,
  viewModel,
  onDelete,
  deleteLoading = false,
}: PipelineDetailContentProps): ReactElement => {
  const hasCustomDates = Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay);
  const buildsNoDataText = formatRangeWindowNoDataText(
    'No builds found',
    viewModel.range,
    hasCustomDates,
  );

  return (
    <DetailPageTemplate
      title={
        <Text role="sectionTitle" component="h1">
          <DetailTitlePrefix label="Pipeline" />
          {viewModel.title}
        </Text>
      }
      headerActions={
        <DetailDeleteHeaderAction
          onDelete={onDelete}
          deleteLoading={deleteLoading}
          dialogTitle="Delete Pipeline"
          dialogDescription="This will permanently delete the pipeline and all associated builds. This action cannot be undone."
          confirmLabel="Delete Pipeline"
        />
      }
      metadata={<PipelineDetailsPanels pipeline={pipeline} />}
      primaryContent={
        <Box sx={{ mt: s('md') }}>
          <Stack gap="md">
            <Box
              sx={{
                borderTop: 1,
                borderBottom: 1,
                borderColor: 'divider',
                py: s('lg'),
              }}
            >
              <PageHeader
                title="Pipeline Builds"
                actions={
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    gap="md"
                    sx={{ alignItems: { xs: 'stretch', md: 'center' } }}
                  >
                    <TagsFilterField
                      value={viewModel.selectedTags}
                      options={viewModel.tagOptions}
                      onChange={viewModel.handleTagsChange}
                      emptyLabel={viewModel.tagOptionsLoading ? 'Loading Tags...' : ''}
                    />
                    <DashboardDateRangeControls
                      value={viewModel.range}
                      onChange={(nextRange) => {
                        viewModel.setRange(nextRange);
                        viewModel.setSelectedStartDay(null);
                        viewModel.setSelectedEndDay(null);
                      }}
                      displayStartDay={viewModel.pickerStartDay}
                      displayEndDay={viewModel.pickerEndDay}
                      isCustomRange={
                        Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay) &&
                        !viewModel.isAllMode
                      }
                      onSelectedStartDayChange={viewModel.handleStartDayChange}
                      onSelectedEndDayChange={viewModel.handleEndDayChange}
                      fullWidthOnMobile
                    />
                  </Stack>
                }
              />
            </Box>
            <Box sx={{ mt: s('md') }}>
              <Stack gap="lg">
                <StatusMetricCardsRow cards={viewModel.pipelineMetricCards} />
                <MetricValueCardsRow items={viewModel.metricValueItems} />
              </Stack>
            </Box>
            <Box sx={{ mt: s('md') }}>
              <ExecutionMetricsChartPanel
                chartTab={viewModel.chartTab}
                tabsAriaLabel="Pipeline detail chart tabs"
                onChartTabChange={viewModel.setChartTab}
                showChartDateClear={viewModel.showChartDateClear}
                onChartDateClear={viewModel.handleChartDateClear}
                chartHeight={360}
                suppressChartAnimation={viewModel.suppressChartAnimation}
                stackedChartData={viewModel.stackedChartData}
                passRateChartData={viewModel.passRateChartData}
                frequencyChartData={viewModel.frequencyChartData}
                durationChartData={viewModel.durationChartData}
                passRateGuideLines={viewModel.passRateGuideLines}
                frequencyGuideLines={viewModel.frequencyGuideLines}
                durationGuideLines={viewModel.durationGuideLines}
                onChartSelect={viewModel.handleChartSelect}
                showLineChartPoints={viewModel.showLineChartPoints}
                frequencySeriesLabel="Builds"
                emptyStateTitle={buildsNoDataText}
              />
            </Box>
            <Box sx={{ mt: s('lg') }}>
              <Panel title="Pipeline Builds" padding="md" gap="none" contentGap="md">
                <BuildsTable
                  persistKey={`pipeline-detail-builds-${viewModel.pipelineId}`}
                  fixedFilters={viewModel.buildsTableFilters}
                  pageSizeOptions={[10]}
                  initialPageSize={10}
                  disablePageSizeSelect
                  noRowsText={buildsNoDataText}
                  showPipelineColumn={false}
                />
              </Panel>
            </Box>
          </Stack>
        </Box>
      }
    />
  );
};
